import type {
    WmlExportFormat,
    CoreExportFormat,
    ExportFormat,
    RawComponentExport,
    ImportExportConverter,
    ImportExportFormatConverter
} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'
import {importFromUrl} from '@wix/document-manager-dynamic-import'
import * as wmlConverterModule from '@wix/document-management-wml-converter'
import * as emlConverterModule from '@wix/document-management-eml-converter'
import * as coreConverterModule from '@wix/document-management-core-converter'
import {deepClone} from '@wix/wix-immutable-proxy'

type FormatConverterType = 'wml'
type ConverterType = FormatConverterType | 'core'
type ConvertersMap = {
    [K in ConverterType]: {
        converterName: string
        getPackageUrl(version: string): string
        converterModule: ImportExportConverter
    }
}

const convertersMap: ConvertersMap = {
    core: {
        converterName: 'document-management-core-converter',
        getPackageUrl: (version: string) =>
            `https://static.parastorage.com/unpkg/@wix/document-management-core-converter@${version}`,
        converterModule: coreConverterModule
    },
    wml: {
        converterName: 'document-management-wml-converter',
        getPackageUrl: (version: string) =>
            `https://static.parastorage.com/unpkg/@wix/document-management-wml-converter@${version}`,
        converterModule: wmlConverterModule
    }
}

const getDmConverterSourceQuery = (): string | undefined => {
    try {
        return new URLSearchParams(window.location.search).get('DmConverterSource') ?? undefined
    } catch (e) {
        /* not browser */
    }
}

const loadConverterByUrl = async <T extends ImportExportConverter>(url: string): Promise<T> => {
    try {
        return await importFromUrl<T>(url)
    } catch (error: any) {
        throw new ReportableError({
            errorType: 'cannotGetConverterPackage',
            message: `could not get document management converter package from url: ${url}`,
            extras: {url, error: error?.message ?? error?.code}
        })
    }
}

const getUrlBySource = (converterName: string, converterSourceQuery?: string): string | undefined => {
    if (!converterSourceQuery) {
        return
    }

    return `${converterSourceQuery}/${converterName}`
}

const getConverterModule = async <T extends ImportExportConverter>(
    componentExport: ExportFormat,
    converterType: ConverterType
): Promise<T> => {
    const {version} = componentExport
    if (!version) {
        throw new ReportableError({
            errorType: 'missingImportExportVersion',
            message: 'ImportExport version is missing'
        })
    }

    const converterSourceQuery = getDmConverterSourceQuery()

    if (converterSourceQuery && !converterSourceQuery.startsWith('http://localhost:')) {
        throw new ReportableError({
            errorType: 'INVALID_DM_CONVERTER_SOURCE',
            message: `DM converter source must be a localhost url`,
            extras: {converterSourceQuery}
        })
    }

    const {converterModule, getPackageUrl, converterName} = convertersMap[converterType]
    if (!converterSourceQuery && version === converterModule.converter.getVersion()) {
        return converterModule as T
    }

    const url = getUrlBySource(converterName, converterSourceQuery) ?? getPackageUrl(version)
    return loadConverterByUrl(url)
}

const convertComponentExportToCoreExport = async (
    componentExport: ExportFormat,
    converterType: FormatConverterType
): Promise<CoreExportFormat> => {
    const {converter} = await getConverterModule<ImportExportFormatConverter>(componentExport, converterType)
    return converter.unpack(deepClone(componentExport))
}

export const convertCoreToRawComponentExport = async (
    componentExport: CoreExportFormat
): Promise<RawComponentExport> => {
    const {converter} = await getConverterModule<ImportExportConverter<CoreExportFormat, RawComponentExport>>(
        componentExport,
        'core'
    )
    return converter.unpack(deepClone(componentExport))
}

export const convertRawToCoreComponentExport = (componentExport: RawComponentExport): CoreExportFormat =>
    coreConverterModule.converter.pack(componentExport)

export const convertWmlToCoreComponentExport = async (componentExport: WmlExportFormat): Promise<CoreExportFormat> =>
    convertComponentExportToCoreExport(componentExport, 'wml')

export const convertWmlToRawComponentExport = async (componentExport: WmlExportFormat): Promise<RawComponentExport> => {
    const coreExport = await convertWmlToCoreComponentExport(componentExport)
    return convertCoreToRawComponentExport(coreExport)
}

export const convertRawToWmlComponentExport = (componentExport: RawComponentExport): WmlExportFormat =>
    wmlConverterModule.converter.pack(convertRawToCoreComponentExport(componentExport))

export const convertCoreToWmlComponentExport = (componentExport: CoreExportFormat): WmlExportFormat =>
    wmlConverterModule.converter.pack(componentExport)

export const convertRawToEmlComponentExport = (
    componentExport: RawComponentExport,
    context?: emlConverterModule.EmlExportContext
): emlConverterModule.EmlExportFormat =>
    emlConverterModule.converter.pack(convertRawToCoreComponentExport(componentExport), context)
